/**
 * Sidebar - Navigation with role-based menu items
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Divider, Avatar,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import PlaceIcon from '@mui/icons-material/Place';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RecyclingIcon from '@mui/icons-material/Recycling';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useAuth } from '../../context/AuthContext';

import AssessmentIcon from '@mui/icons-material/Assessment';

const DRAWER_WIDTH = 250;

const menus = {
  super_admin: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    { text: 'Residents', icon: <PeopleIcon />, path: '/residents' },
    { text: 'Collectors', icon: <PeopleIcon />, path: '/collectors' },
    { text: 'Estates', icon: <ApartmentIcon />, path: '/estates' },
    { text: 'Waste Categories', icon: <CategoryIcon />, path: '/categories' },
    { text: 'Bin Stations', icon: <PlaceIcon />, path: '/stations' },
    { text: 'Collection Schedule', icon: <EventIcon />, path: '/schedule' },
    { text: 'Billing', icon: <AttachMoneyIcon />, path: '/billing' },
    { divider: true },
    { text: 'Support', icon: <SupportAgentIcon />, path: '/support' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ],
  collector: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/collector' },
    { divider: true },
    { text: 'Support', icon: <SupportAgentIcon />, path: '/support' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ],
  resident: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/portal' },
    { text: 'Dispose Waste', icon: <DeleteOutlineIcon />, path: '/dispose' },
    { text: 'My Reports', icon: <AssessmentIcon />, path: '/reports' },
    { divider: true },
    { text: 'Support', icon: <SupportAgentIcon />, path: '/support' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ],
};

const Sidebar = ({ open, onClose, isMobile }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();

  const items = menus[user?.role] || menus.resident;

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <RecyclingIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.2 }}>Smart Waste Management System</Typography>
      </Box>

      <Box sx={{ px: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
          {user?.name?.charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user?.name || 'User'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {user?.role?.replace('_', ' ') || 'Resident'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2 }} />

      <List sx={{ px: 1.5, py: 1, flexGrow: 1 }}>
        {items.map((item, i) =>
          item.divider ? (
            <Divider key={i} sx={{ my: 1 }} />
          ) : (
            <ListItemButton
              key={item.text}
              selected={pathname === item.path}
              onClick={() => handleNav(item.path)}
              sx={{
                borderRadius: 2, mb: 0.5, py: 1,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: '#fff',
                  '& .MuiListItemIcon-root': { color: '#fff' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: pathname === item.path ? '#fff' : 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: 14, fontWeight: pathname === item.path ? 600 : 400 }}
              />
            </ListItemButton>
          )
        )}
      </List>

      <Box sx={{ p: 2 }}>
        <ListItemButton
          sx={{ borderRadius: 2 }}
          selected={pathname === '/help-center'}
          onClick={() => handleNav('/help-center')}
        >
          <ListItemIcon sx={{ minWidth: 40 }}><HelpOutlineIcon /></ListItemIcon>
          <ListItemText primary="Help Center" primaryTypographyProps={{ fontSize: 14 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
